/**
 * Shared Chart Page
 * View shared charts via short link
 * Route: /s/:code
 */

import { devLog, devError } from '../utils/devLog';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Text, Spinner, VStack, Alert, AlertIcon, AlertTitle, AlertDescription, Button } from '@chakra-ui/react';
import { shortLinkApiService } from '../services/ShortLinkApiService';
import { SharedChartData, SharedViewMode } from '../types/share.types';
import { globalEventStream, createEvent } from '../components/common/AstrologyChart/universal_architecture';
import MainLayout from '../components/layout/MainLayout';

/**
 * Inner component - renders MainLayout with shared chart data
 * Chart loading happens inside MainLayout's MultipleChartsProvider
 */
function SharedChartContent({
  chartData,
  viewMode,
  onViewModeChange
}: {
  chartData: SharedChartData;
  viewMode: SharedViewMode;
  onViewModeChange: (mode: SharedViewMode) => void;
}) {
  devLog('📄 [SharedChartContent] Rendered with viewMode:', viewMode);

  return (
    <MainLayout
      shareMode={true}
      sharedChartData={chartData}
      sharedViewMode={viewMode}
      onSharedViewModeChange={onViewModeChange}
    />
  );
}

/**
 * Shared Chart Page Component
 */
export function SharedChartPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  const [chartData, setChartData] = useState<SharedChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<SharedViewMode>('author');

  // Load shared chart data ONCE on mount (always with JWT if author)
  useEffect(() => {
    if (!code) {
      setError('Invalid share link - no code provided');
      setLoading(false);
      return;
    }

    const loadSharedChart = async () => {
      try {
        setLoading(true);
        setError(null);

        devLog(`🔗 Loading shared chart: ${code}`);

        // ALWAYS fetch with JWT (author view) - filtering happens locally
        const data = await shortLinkApiService.getSharedChart(code, false);

        devLog('✅ Shared chart loaded:', {
          hasMetadata: !!data.chart_metadata,
          configuredFields: Object.keys(data.chart_metadata || {}),
          isAuthor: data.is_author,
        });

        setChartData(data);

        // Set initial view mode based on whether user is author
        const initialViewMode: SharedViewMode = data.is_author ? 'author' : 'guest';
        setViewMode(initialViewMode);

        // Set window.sharedViewMode for global access (used in Settings and TabChartRenderer)
        if (typeof window !== 'undefined') {
          window.sharedViewMode = initialViewMode;

          // For guest mode, force showLogo to true (override localStorage)
          if (initialViewMode === 'guest') {
            window.globalShowLogo = true;
            devLog('✅ [SharedChartPage] Guest mode detected - forcing showLogo = true');

            // Emit event to update already-rendered charts
            globalEventStream.emit({
              id: `logo-visibility-changed-guest-${Date.now()}`,
              type: 'settings.logo.changed',
              source: 'shared-chart-page',
              timestamp: Date.now(),
              showLogo: true
            });
          }
        }

        // Emit event to global event stream for chart rendering
        globalEventStream.emit(createEvent(
          'chart:shared:loaded',
          'shared-chart-page',
          {
            chartData: data,
            shortCode: code,
            timestamp: Date.now(),
          }
        ));

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load shared chart';
        devError('❌ Failed to load shared chart:', err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadSharedChart();
  }, [code]); // ✅ Removed viewMode dependency - no refetch on mode change

  // Handle view mode change (local state only - no server request)
  const handleViewModeChange = (mode: SharedViewMode) => {
    devLog(`🔄 Switching view mode: ${viewMode} → ${mode} (local only, no API call)`);
    setViewMode(mode);

    // Update window.sharedViewMode for global access
    if (typeof window !== 'undefined') {
      window.sharedViewMode = mode;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="center" justify="center" minH="60vh">
          <Spinner size="xl" thickness="4px" color="blue.500" />
          <Text fontSize="lg" color="gray.600">
            Loading shared chart...
          </Text>
        </VStack>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minH="60vh"
          borderRadius="lg"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Unable to Load Chart
          </AlertTitle>
          <AlertDescription maxWidth="sm" mb={4}>
            {error}
          </AlertDescription>
          <Button
            onClick={() => navigate('/')}
            colorScheme="blue"
            variant="outline"
          >
            Go to Home
          </Button>
        </Alert>
      </Container>
    );
  }

  // Chart loaded state
  if (!chartData) {
    return null;
  }

  // Render SharedChartContent directly - MainLayout provides MultipleChartsProvider
  return (
    <SharedChartContent
      chartData={chartData}
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
    />
  );
}
